import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, MenuItem, Paper, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WebhookIcon from '@mui/icons-material/Webhook';
import {
  createFlowTrigger, deleteFlowTrigger, listFlowTriggers, runFlowTriggerNow, updateFlowTrigger, webhookUrl,
} from '../../../api/triggers.api';

export const SCHEDULE_PRESETS = [
  { cron: '0 * * * *', label: 'Every hour' },
  { cron: '0 9 * * *', label: 'Every day at 09:00 UTC' },
  { cron: '0 9 * * 1-5', label: 'Weekdays at 09:00 UTC' },
  { cron: '0 9 * * 1', label: 'Mondays at 09:00 UTC' },
  { cron: 'custom', label: 'Custom (cron)' },
];

export function describeCron(cron) {
  return SCHEDULE_PRESETS.find((p) => p.cron === cron)?.label || `cron “${cron}” (UTC)`;
}

const when = (d) => (d ? new Date(d).toLocaleString() : '');

function parseInput(text) {
  if (!text.trim()) return { value: {} };
  try {
    const v = JSON.parse(text);
    return v && typeof v === 'object' && !Array.isArray(v) ? { value: v } : { error: 'Inputs must be a JSON object' };
  } catch {
    return { error: 'Inputs are not valid JSON' };
  }
}

function RunHistory({ runs }) {
  if (!runs?.length) return <Typography variant="caption" color="text.secondary">No runs yet</Typography>;
  return (
    <Stack spacing={0.5} data-testid="trigger-runs">
      {runs.slice(0, 5).map((r) => (
        <Typography
          key={`${r.at}-${r.source}`}
          variant="caption"
          color={r.ok ? 'success.main' : 'error.main'}
          data-testid="trigger-run"
          data-ok={r.ok ? 'true' : 'false'}
          sx={{ wordBreak: 'break-word' }}
        >
          {`${r.ok ? 'Worked' : 'Failed'} · ${r.source} · ${when(r.at)}${r.durationMs != null ? ` · ${(r.durationMs / 1000).toFixed(1)} s` : ''}`}
          {r.ok ? '' : ` · ${r.error}`}
        </Typography>
      ))}
    </Stack>
  );
}

function TriggerCard({
  trigger, onChange, onDelete, readOnly,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [lastRun, setLastRun] = useState(null);

  const toggle = async (enabled) => {
    const { trigger: t, error: err } = await updateFlowTrigger(trigger.id, { enabled });
    if (err) setError(err);
    else onChange(t);
  };

  const runNow = async () => {
    setBusy(true);
    setError('');
    const { run, trigger: t, error: err } = await runFlowTriggerNow(trigger.id);
    setBusy(false);
    if (err) setError(err);
    if (run) setLastRun(run);
    if (t) onChange(t);
  };

  const isSchedule = trigger.kind === 'schedule';
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }} data-testid={`trigger-${trigger.kind}`}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {isSchedule ? <ScheduleIcon fontSize="small" color="primary" /> : <WebhookIcon fontSize="small" color="primary" />}
          <Typography variant="subtitle2" fontWeight={700}>
            {trigger.name || (isSchedule ? 'Schedule' : 'Webhook')}
          </Typography>
          <Chip size="small" variant="outlined" label={isSchedule ? describeCron(trigger.cron) : `URL ending ${trigger.tokenHint}`} />
          <Box sx={{ flex: 1 }} />
          <Tooltip title={trigger.enabled ? 'On' : 'Off'}>
            <Switch
              size="small"
              checked={trigger.enabled}
              disabled={readOnly}
              onChange={(e) => toggle(e.target.checked)}
              inputProps={{ 'aria-label': `Turn ${trigger.name || trigger.kind} on or off` }}
            />
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={busy ? <CircularProgress size={14} /> : <PlayArrowIcon />}
            disabled={busy || readOnly}
            onClick={runNow}
          >
            Run now
          </Button>
          <IconButton size="small" aria-label={`Delete ${trigger.name || trigger.kind} trigger`} disabled={readOnly} onClick={() => onDelete(trigger.id)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
        {isSchedule && trigger.enabled && trigger.nextRunAt && (
          <Typography variant="caption" color="text.secondary">{`Next run ${when(trigger.nextRunAt)}`}</Typography>
        )}
        {Object.keys(trigger.input || {}).length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
            {`Inputs ${JSON.stringify(trigger.input)}`}
          </Typography>
        )}
        {lastRun && (
          <Alert severity={lastRun.ok ? 'success' : 'error'} sx={{ py: 0 }} data-testid="trigger-last-run">
            {lastRun.ok ? 'The flow finished.' : `The run failed: ${lastRun.error}`}
            {lastRun.ok && lastRun.result != null && (
              <Typography
                component="pre"
                variant="caption"
                sx={{
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, maxHeight: 160, overflow: 'auto',
                }}
              >
                {JSON.stringify(lastRun.result, null, 2)}
              </Typography>
            )}
          </Alert>
        )}
        <RunHistory runs={trigger.runs} />
        {error && <Alert severity="warning" sx={{ py: 0 }}>{error}</Alert>}
      </Stack>
    </Paper>
  );
}

function NewTrigger({ flowId, onCreated }) {
  const [kind, setKind] = useState('');
  const [name, setName] = useState('');
  const [preset, setPreset] = useState(SCHEDULE_PRESETS[1].cron);
  const [cron, setCron] = useState('');
  const [inputText, setInputText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setKind('');
    setName('');
    setCron('');
    setInputText('');
    setError('');
  };

  const create = async () => {
    const parsed = parseInput(inputText);
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setBusy(true);
    const { trigger, webhookPath, error: err } = await createFlowTrigger({
      flowId,
      kind,
      name,
      input: parsed.value,
      ...(kind === 'schedule' ? { cron: preset === 'custom' ? cron.trim() : preset } : {}),
    });
    setBusy(false);
    if (err) {
      setError(err);
      return;
    }
    onCreated(trigger, webhookPath);
    reset();
  };

  if (!kind) {
    return (
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="outlined" startIcon={<WebhookIcon />} onClick={() => setKind('webhook')}>Add webhook</Button>
        <Button size="small" variant="outlined" startIcon={<ScheduleIcon />} onClick={() => setKind('schedule')}>Add schedule</Button>
      </Stack>
    );
  }
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Stack spacing={1.25}>
        <Typography variant="subtitle2" fontWeight={700}>{kind === 'webhook' ? 'New webhook' : 'New schedule'}</Typography>
        <TextField size="small" label="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
        {kind === 'schedule' && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField select size="small" label="When" value={preset} onChange={(e) => setPreset(e.target.value)} sx={{ minWidth: 240 }}>
              {SCHEDULE_PRESETS.map((p) => <MenuItem key={p.cron} value={p.cron}>{p.label}</MenuItem>)}
            </TextField>
            {preset === 'custom' && (
              <TextField
                size="small"
                label="Cron (minute hour day month weekday, UTC)"
                placeholder="30 8 * * 1-5"
                value={cron}
                onChange={(e) => setCron(e.target.value)}
                sx={{ flex: 1 }}
              />
            )}
          </Stack>
        )}
        <TextField
          size="small"
          label="Inputs for each run (JSON object, optional)"
          placeholder='{"keyword": "data engineer"}'
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          multiline
          minRows={2}
          helperText={kind === 'webhook'
            ? 'The JSON body a caller sends is added on top of these inputs.'
            : 'Runs use the flow’s sample input plus these values.'}
        />
        {error && <Alert severity="warning" sx={{ py: 0 }}>{error}</Alert>}
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" disabled={busy || (kind === 'schedule' && preset === 'custom' && !cron.trim())} onClick={create}>
            {busy ? 'Saving…' : 'Create'}
          </Button>
          <Button size="small" onClick={reset}>Cancel</Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/** Settings tab section: start this flow from a webhook URL or on a schedule. */
export default function FlowTriggersPanel({ flowId, readOnly = false }) {
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const { triggers: list, error: err } = await listFlowTriggers(flowId);
    setTriggers(list);
    setError(err || '');
    setLoading(false);
  }, [flowId]);

  useEffect(() => { load(); }, [load]);

  const replace = (t) => setTriggers((prev) => prev.map((x) => (x.id === t.id ? t : x)));
  const remove = async (id) => {
    const { error: err } = await deleteFlowTrigger(id);
    if (err) setError(err);
    else setTriggers((prev) => prev.filter((x) => x.id !== id));
  };

  return (
    <Stack spacing={1.5} data-testid="flow-triggers">
      <Typography variant="body2" color="text.secondary">
        Start this flow without opening the Studio. A webhook runs it whenever another system calls its secret URL;
        a schedule runs it on a timer. Runs use your account and this workspace&apos;s connections.
      </Typography>
      {loading && <CircularProgress size={18} />}
      {error && <Alert severity="warning">{error}</Alert>}
      {newUrl && (
        <Alert severity="success" data-testid="webhook-url-alert" onClose={() => setNewUrl('')}>
          <Typography variant="body2" fontWeight={700}>Copy this webhook URL now. It won&apos;t be shown again.</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }} data-testid="webhook-url">{newUrl}</Typography>
            <IconButton
              size="small"
              aria-label="Copy webhook URL"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(newUrl);
                  setCopied(true);
                } catch { /* clipboard unavailable */ }
              }}
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
            {copied && <Typography variant="caption">Copied</Typography>}
          </Stack>
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
            POST JSON to it. Add ?wait=true to get the flow’s result back in the response.
          </Typography>
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }} data-testid="webhook-header-hint">
            To keep the secret out of URLs, POST to {webhookUrl('/api/v2/hooks')} with the header
            {' '}<Box component="code" sx={{ wordBreak: 'break-all' }}>X-Webhook-Token: {newUrl.split('/').pop()}</Box>.
          </Typography>
        </Alert>
      )}
      {triggers.map((t) => (
        <TriggerCard key={t.id} trigger={t} onChange={replace} onDelete={remove} readOnly={readOnly} />
      ))}
      {!readOnly && (
        <NewTrigger
          flowId={flowId}
          onCreated={(t, path) => {
            setTriggers((prev) => [...prev, t]);
            if (path) {
              setNewUrl(webhookUrl(path));
              setCopied(false);
            }
          }}
        />
      )}
    </Stack>
  );
}
